import localFont from 'next/font/local';

// Self-hosted fonts from @placekit/uikit, served by next/font from /_next/static/media/.
// Exposed to the uikit Tailwind preset through --font-switzer / --font-fira-code (see _app.jsx).
// Paths climb one directory only: Turbopack fails to resolve `../../node_modules/...`.
// uikit's "Switzer Fallback" / "Fira Code Fallback" metric faces (global-nofonts.css) follow
// these families in the preset stack, so next/font's own fallback is disabled.

// Switzer: only the normal file goes through next/font, which preloads every file of a call.
// The italic face (33 KB, used by a few blog articles) is declared by hand in `fontVariables`
// under the same family name, from the file emitted as a plain static asset, so the browser
// fetches it only when italic text renders. A separate localFont call would be another family,
// and CSS font matching synthesizes an oblique instead of switching families on style.
export const switzer = localFont({
  src: [
    {
      path: '../node_modules/@placekit/uikit/src/styles/fonts/Switzer-Variable.woff2',
      weight: '100 900',
      style: 'normal',
    },
  ],
  display: 'swap',
  adjustFontFallback: false,
});

// emitted to /_next/static/media/ by the bundler (webpack and Turbopack), never preloaded;
// during SSR the base is a file: URL, so keep the path only
const switzerItalicUrl = new URL(
  '../node_modules/@placekit/uikit/src/styles/fonts/Switzer-VariableItalic.woff2',
  import.meta.url,
);
const switzerItalicHref = `${switzerItalicUrl.pathname}${switzerItalicUrl.search}`;

// Fira Code: one call per subset, each restricted by its unicode-range (same ranges as
// uikit's fonts.css), so a subset is only downloaded when a page renders its characters
// (e.g. symbols2 for box-drawing trees). Not preloaded: only code samples use it.
export const firaCodeLatin = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-latin-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [
    {
      prop: 'unicode-range',
      value:
        'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
    },
  ],
});

export const firaCodeLatinExt = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-latin-ext-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [
    {
      prop: 'unicode-range',
      value:
        'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF',
    },
  ],
});

export const firaCodeSymbols2 = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-symbols2-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [
    { prop: 'unicode-range', value: 'U+2000-2001, U+2004-2008, U+200A, U+23B8-23BD, U+2500-259F' },
  ],
});

export const firaCodeGreek = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-greek-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [
    {
      prop: 'unicode-range',
      value: 'U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF',
    },
  ],
});

export const firaCodeGreekExt = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-greek-ext-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [{ prop: 'unicode-range', value: 'U+1F00-1FFF' }],
});

export const firaCodeCyrillic = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-cyrillic-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [
    { prop: 'unicode-range', value: 'U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116' },
  ],
});

export const firaCodeCyrillicExt = localFont({
  src: '../node_modules/@placekit/uikit/src/styles/fonts/fira-code-cyrillic-ext-wght-normal.woff2',
  weight: '300 700',
  style: 'normal',
  display: 'swap',
  preload: false,
  adjustFontFallback: false,
  declarations: [
    {
      prop: 'unicode-range',
      value: 'U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F',
    },
  ],
});

// Latin first: where ranges overlap (U+2000-206F, combining marks), latin wins, as in uikit's
// fonts.css where it is declared last.
const firaCodeSubsets = [
  firaCodeLatin,
  firaCodeLatinExt,
  firaCodeSymbols2,
  firaCodeGreek,
  firaCodeGreekExt,
  firaCodeCyrillic,
  firaCodeCyrillicExt,
];

// Each `style.fontFamily` is "'<generated family>'" (no next/font fallback since
// adjustFontFallback is false), so they can be joined into one stack.
export const fontVariables = `@font-face{font-family:${switzer.style.fontFamily};font-style:italic;font-weight:100 900;font-display:swap;src:url(${switzerItalicHref}) format('woff2')}:root{--font-switzer:${switzer.style.fontFamily};--font-fira-code:${firaCodeSubsets
  .map((font) => font.style.fontFamily)
  .join(',')};}`;
